import { useState, useMemo, useEffect, useRef } from "react";
import {
  createFileRoute,
  useSearch,
  useNavigate,
} from "@tanstack/react-router";
import { Button, useToast } from "@repo/ui";
import { Plus, Bell } from "lucide-react";
import { formatToApiDate } from "@repo/utils";
import { RemindersList } from "@/components/reminders/reminders-list";
import { ReminderDetailPanel } from "@/components/reminders/reminder-detail-panel";
import { ReminderFiltersBar } from "@/components/reminders/reminder-filters";
import { CreateReminderDialog } from "@/components/reminders/create-reminder-dialog";
import { CompleteReminderDialog } from "@/components/reminders/complete-reminder-dialog";
import { useRemindersContext } from "@/context/reminders-context";
import type {
  Reminder,
  ReminderFilters,
  ReminderReferenceType,
} from "@/hooks/use-reminders";

interface RemindersSearchParams {
  id?: string;
  action?: string;
  referenceType?: string;
  referenceId?: string;
  referenceName?: string;
  patientId?: string;
  patientName?: string;
}

export const Route = createFileRoute("/_app/reminders")({
  component: RemindersPage,
  validateSearch: (search: Record<string, unknown>): RemindersSearchParams => ({
    id: search.id as string | undefined,
    action: search.action as string | undefined,
    referenceType: search.referenceType as string | undefined,
    referenceId: search.referenceId as string | undefined,
    referenceName: search.referenceName as string | undefined,
    patientId: search.patientId as string | undefined,
    patientName: search.patientName as string | undefined,
  }),
});

function RemindersPage() {
  const {
    reminders: allReminders,
    isLoading,
    createReminder,
    updateReminder,
    deleteReminder,
    toggleComplete,
  } = useRemindersContext();

  const search = useSearch({ from: "/_app/reminders" });
  const navigate = useNavigate();

  const [prefill, setPrefill] = useState<{
    referenceType?: ReminderReferenceType;
    referenceId?: string;
    referenceName?: string;
  }>({});
  const [patientPrefill, setPatientPrefill] = useState<{
    patientId: string;
    patientName: string;
  } | null>(null);

  const [filters, setFilters] = useState<ReminderFilters>({
    visibility: "all",
    status: "active",
    priority: "all",
  });

  // Client-side filtering
  const reminders = useMemo(() => {
    return allReminders.filter((r) => {
      if (filters.visibility && filters.visibility !== "all") {
        if (r.visibility !== filters.visibility) return false;
      }
      if (filters.status && filters.status !== "all") {
        if (filters.status === "active" && r.is_completed) return false;
        if (filters.status === "completed" && !r.is_completed) return false;
      }
      if (filters.priority && filters.priority !== "all") {
        if (r.priority !== filters.priority) return false;
      }
      if (filters.patientLinked) {
        if (!r.patient_id) return false;
      }
      return true;
    });
  }, [allReminders, filters]);

  const today = formatToApiDate(new Date());
  const overdueCount = reminders.filter(
    (r) => !r.is_completed && r.due_date < today
  ).length;
  const dueTodayCount = reminders.filter(
    (r) => !r.is_completed && r.due_date === today
  ).length;
  const activeCount = reminders.filter((r) => !r.is_completed).length;

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<Reminder | null>(
    null
  );
  const [completingReminder, setCompletingReminder] = useState<Reminder | null>(
    null
  );
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const { toast } = useToast();

  // Auto-open create dialog when navigated with ?action=create&referenceType=...
  useEffect(() => {
    if (search.action === "create") {
      if (search.referenceType && search.referenceId) {
        setPrefill({
          referenceType: search.referenceType as ReminderReferenceType,
          referenceId: search.referenceId,
          referenceName: search.referenceName,
        });
      }
      if (search.patientId && search.patientName) {
        setPatientPrefill({
          patientId: search.patientId,
          patientName: search.patientName,
        });
      } else {
        setPatientPrefill(null);
      }
      setShowCreateDialog(true);
      // Clear search params so re-renders / back navigation don't re-trigger
      navigate({ to: "/reminders", search: {}, replace: true });
    }
  }, [
    search.action,
    search.referenceType,
    search.referenceId,
    search.referenceName,
    search.patientId,
    search.patientName,
    navigate,
  ]);

  // Auto-open detail panel when navigated with ?id=
  const didOpenIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!search.id || didOpenIdRef.current === search.id || isLoading) return;
    const match = allReminders.find((r) => r.id === search.id);
    if (match) {
      didOpenIdRef.current = search.id;
      setSelectedReminder(match);
      navigate({ to: "/reminders", search: {}, replace: true });
    } else if (!isLoading) {
      didOpenIdRef.current = search.id;
      navigate({ to: "/reminders", search: {}, replace: true });
    }
  }, [search.id, allReminders, isLoading, navigate]);

  const handleDelete = async (id: string) => {
    const success = await deleteReminder(id);
    if (success) {
      toast({ title: "Reminder deleted", variant: "success" });
      if (selectedReminder?.id === id) setSelectedReminder(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold text-foreground">Reminders</h1>
          {overdueCount > 0 && (
            <span className="rounded-full bg-destructive px-2 py-0.5 text-xs font-medium text-white">
              {overdueCount} overdue
            </span>
          )}
        </div>
        <Button
          data-testid="reminder-create-button"
          onClick={() => setShowCreateDialog(true)}
          size="sm"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          New Reminder
        </Button>
      </div>

      <ReminderFiltersBar
        filters={filters}
        onFiltersChange={setFilters}
        overdueCount={overdueCount}
        dueTodayCount={dueTodayCount}
        activeCount={activeCount}
      />

      <RemindersList
        reminders={reminders}
        isLoading={isLoading}
        onToggleComplete={(id) => {
          const reminder = reminders.find((r) => r.id === id);
          if (reminder) setCompletingReminder(reminder);
        }}
        onEdit={(reminder) => setEditingReminder(reminder)}
        onDelete={handleDelete}
        onViewDetail={(reminder) => setSelectedReminder(reminder)}
      />

      <ReminderDetailPanel
        reminder={selectedReminder}
        open={!!selectedReminder}
        onClose={() => setSelectedReminder(null)}
        onEdit={(reminder) => {
          setSelectedReminder(null);
          setEditingReminder(reminder);
        }}
        onDelete={(id) => {
          setSelectedReminder(null);
          handleDelete(id);
        }}
        onToggleComplete={(id) => {
          const reminder = reminders.find((r) => r.id === id);
          if (reminder) {
            setSelectedReminder(null);
            setCompletingReminder(reminder);
          }
        }}
      />

      <CreateReminderDialog
        open={showCreateDialog || !!editingReminder}
        onClose={() => {
          setShowCreateDialog(false);
          setEditingReminder(null);
          setPrefill({});
          setPatientPrefill(null);
        }}
        onSubmit={async (input) => {
          const result = await createReminder(input);
          if (result) {
            toast({ title: "Reminder created", variant: "success" });
            setShowCreateDialog(false);
            setPrefill({});
            setPatientPrefill(null);
          }
          return result;
        }}
        onUpdate={async (id, input) => {
          const result = await updateReminder(id, input);
          if (result) {
            toast({ title: "Reminder updated", variant: "success" });
            setEditingReminder(null);
          }
          return result;
        }}
        editingReminder={editingReminder}
        prefillReferenceType={prefill.referenceType}
        prefillReferenceId={prefill.referenceId}
        prefillReferenceName={prefill.referenceName}
        prefillPatientId={patientPrefill?.patientId}
        prefillPatientName={patientPrefill?.patientName}
      />

      <CompleteReminderDialog
        open={!!completingReminder}
        onClose={() => setCompletingReminder(null)}
        onConfirm={async (note) => {
          if (completingReminder) {
            await toggleComplete(completingReminder.id, note);
            setCompletingReminder(null);
          }
        }}
        reminderTitle={completingReminder?.title ?? ""}
        isPharmacyReminder={completingReminder?.visibility === "pharmacy"}
      />
    </div>
  );
}
